import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, authHeader } from '../lib/api'
import type { RecruiterProfile } from '@tnf-recruit/shared'
import { useAuth } from './useAuth'

interface DashboardData {
  totalStudents: number
  savedProfiles: number
}

/**
 * A recruiter's school is set from their verified signup selection and isn't
 * editable, so profile saves omit it entirely.
 */
type SchoolRef = { id: string }

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useRecruiterProfile(options?: { enabled?: boolean }) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()

  const profile = useQuery({
    queryKey: ['recruiterProfile'],
    enabled: options?.enabled !== false,
    queryFn: async () => {
      const token = await getAccessToken()
      const { data } = await apiClient.get('/recruiters/profile', authHeader(token))
      return data.data as RecruiterProfile | null
    },
  })

  const saveProfile = useMutation({
    mutationFn: async (body: {
      school?: SchoolRef
      position: string
      bio?: string
      contactInfo: RecruiterProfile['contactInfo']
      recruitingFocus: RecruiterProfile['recruitingFocus']
    }) => {
      const token = await getAccessToken()
      const { data } = await apiClient.post('/recruiters/profile', body, authHeader(token))
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recruiterProfile'] })
    },
  })

  const updateProfile = useMutation({
    mutationFn: async (
      body: Partial<{
        school: SchoolRef
        position: string
        bio: string
        contactInfo: RecruiterProfile['contactInfo']
        recruitingFocus: RecruiterProfile['recruitingFocus']
      }>
    ) => {
      const token = await getAccessToken()
      const { data } = await apiClient.patch('/recruiters/profile', body, authHeader(token))
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recruiterProfile'] })
    },
  })

  const dashboard = useQuery({
    queryKey: ['recruiterDashboard'],
    queryFn: async () => {
      const token = await getAccessToken()
      const { data } = await apiClient.get('/recruiters/dashboard', authHeader(token))
      return data.data as DashboardData
    },
  })

  return { profile, saveProfile, updateProfile, dashboard }
}
